﻿import { useEffect, useMemo, useState } from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import BlockIcon from "@mui/icons-material/Block"
import RefreshIcon from "@mui/icons-material/Refresh"
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"
import PriceChangeRoundedIcon from "@mui/icons-material/PriceChangeRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createProductPrice,
  deactivateProductPrice,
  getProductPriceFormData,
  getProductPrices,
  updateProductPrice,
} from "../../services/productPriceService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const DEFAULT_FORM = {
  product_id: "",
  kin_mousse_price: "",
  maison_djo_margin_percent: "25",
  depot_relais_margin_percent: "25",
  terrain_margin_percent: "20",
  is_active: true,
  notes: "",
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number = Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatMoney(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


function formatPercent(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  )
}


function calculatePrice(
  basePrice,
  marginPercent
) {
  const base = toNumber(basePrice)
  const margin = toNumber(marginPercent)

  if (base <= 0) {
    return 0
  }

  return (
    base +
    (base * margin) / 100
  )
}


function getErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  return (
    error?.message ||
    fallback
  )
}


function getPriceStatus(price) {
  if (price?.is_active) {
    return {
      label: "Actif",
      color: "success",
      variant: "filled",
    }
  }

  if (price?.configured) {
    return {
      label: "Inactif",
      color: "default",
      variant: "outlined",
    }
  }

  return {
    label: "Non configuré",
    color: "warning",
    variant: "filled",
  }
}


// =========================================================
// COMPACT KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.08)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.4,
          sm: 1.6,
        },

        py: {
          xs: 1.25,
          sm: 1.35,
        },

        borderRadius: 3,

        minWidth: 0,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",

        bgcolor: "#ffffff",

        transition:
          "transform 0.18s ease, box-shadow 0.18s ease",

        "&:hover": {
          transform:
            "translateY(-1px)",

          boxShadow:
            "0 9px 24px rgba(15, 23, 42, 0.065)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.25,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            borderRadius: 2.5,

            display: "grid",

            placeItems: "center",

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,

            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.8,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontWeight: 900,

                color: "#0f172a",

                fontSize: {
                  xs: 20,
                  sm: 22,
                },

                lineHeight: 1,
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",

                textOverflow: "ellipsis",

                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",

              textOverflow: "ellipsis",

              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

function ProductPricesPage() {
  const theme = useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    prices,
    setPrices,
  ] = useState([])

  const [
    summary,
    setSummary,
  ] = useState({})

  const [
    formData,
    setFormData,
  ] = useState({
    products: [],
    permissions: {},
    context: {},
    defaults: {},
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    filters,
    setFilters,
  ] = useState({
    search: "",
    configured: "",
    is_active: "",
  })


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    page,
    setPage,
  ] = useState(0)


  // =======================================================
  // DIALOG
  // =======================================================

  const [
    dialogOpen,
    setDialogOpen,
  ] = useState(false)

  const [
    editingPrice,
    setEditingPrice,
  ] = useState(null)

  const [
    form,
    setForm,
  ] = useState({
    ...DEFAULT_FORM,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // PERMISSIONS
  // =======================================================

  const permissions =
    formData.permissions || {}


  const canManageFactory =
    Boolean(
      permissions.can_manage_factory_price
    )


  const canManageMaisonDjo =
    Boolean(
      permissions.can_manage_maison_djo_price
    )


  const canManageDepotRelais =
    Boolean(
      permissions.can_manage_depot_relais_price
    )


  const canManageTerrain =
    Boolean(
      permissions.can_manage_terrain_price
    )


  const canSave =
    Boolean(
      permissions.can_update
    )


  // =======================================================
  // SELECTED PRODUCT
  // =======================================================

  const selectedProduct =
    useMemo(() => {
      return (
        formData.products.find(
          (product) =>
            String(product.id) ===
            String(
              form.product_id
            )
        ) || null
      )
    }, [
      form.product_id,
      formData.products,
    ])


  // =======================================================
  // COMPUTED PRICES
  // =======================================================

  const computedPrices =
    useMemo(() => {
      const kinMoussePrice =
        toNumber(
          form.kin_mousse_price
        )

      const maisonDjoPrice =
        calculatePrice(
          kinMoussePrice,
          form.maison_djo_margin_percent
        )

      const depotRelaisPrice =
        calculatePrice(
          kinMoussePrice,
          form.depot_relais_margin_percent
        )

      const terrainPrice =
        calculatePrice(
          kinMoussePrice,
          form.terrain_margin_percent
        )

      return {
        kinMoussePrice,
        maisonDjoPrice,
        depotRelaisPrice,
        terrainPrice,
      }
    }, [
      form.kin_mousse_price,
      form.maison_djo_margin_percent,
      form.depot_relais_margin_percent,
      form.terrain_margin_percent,
    ])


  // =======================================================
  // STRICT PAGINATION : 10 ROWS ONLY
  // =======================================================

  const paginatedPrices =
    useMemo(() => {
      const start =
        page *
        ROWS_PER_PAGE

      const end =
        start +
        ROWS_PER_PAGE

      return prices.slice(
        start,
        end
      )
    }, [
      prices,
      page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          prices.length /
            ROWS_PER_PAGE
        )
      )

    if (
      page >= totalPages
    ) {
      setPage(
        totalPages - 1
      )
    }
  }, [
    prices.length,
    page,
  ])


  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    loadPage()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // LOAD PAGE
  // =======================================================

  async function loadPage() {
    setLoading(true)

    try {
      const [
        data,
        list,
      ] =
        await Promise.all([
          getProductPriceFormData(),
          getProductPrices(),
        ])

      setFormData({
        products:
          data.products || [],

        permissions:
          data.permissions || {},

        context:
          data.context || {},

        defaults:
          data.defaults || {},
      })

      setPrices(
        list.items || []
      )

      setSummary(
        list.summary || {}
      )

      setPage(0)
    } catch (error) {
      showError(
        getErrorMessage(
          error,
          "Impossible de charger les prix produits."
        )
      )
    } finally {
      setLoading(false)
    }
  }


  // =======================================================
  // LOAD FILTERED PRICES
  // =======================================================

  async function loadPrices() {
    setLoading(true)

    try {
      const list =
        await getProductPrices({
          search:
            filters.search,

          configured:
            filters.configured,

          is_active:
            filters.is_active,
        })

      setPrices(
        list.items || []
      )

      setSummary(
        list.summary || {}
      )

      setPage(0)
    } catch (error) {
      showError(
        getErrorMessage(
          error,
          "Impossible de charger les prix produits."
        )
      )
    } finally {
      setLoading(false)
    }
  }


  // =======================================================
  // SNACKBAR
  // =======================================================

  function showSuccess(message) {
    setSnackbar({
      open: true,
      severity: "success",
      message,
    })
  }


  function showError(message) {
    setSnackbar({
      open: true,
      severity: "error",
      message,
    })
  }


  function closeSnackbar() {
    setSnackbar(
      (current) => ({
        ...current,
        open: false,
      })
    )
  }


  // =======================================================
  // DEFAULT FORM
  // =======================================================

  function getDefaultForm() {
    const defaults =
      formData.defaults || {}

    return {
      ...DEFAULT_FORM,

      maison_djo_margin_percent:
        String(
          defaults.maison_djo_margin_percent ??
            25
        ),

      depot_relais_margin_percent:
        String(
          defaults.depot_relais_margin_percent ??
            25
        ),

      terrain_margin_percent:
        String(
          defaults.terrain_margin_percent ??
            20
        ),
    }
  }


  // =======================================================
  // OPEN CREATE
  // =======================================================

  function openCreateDialog() {
    setEditingPrice(null)

    setForm(
      getDefaultForm()
    )

    setDialogOpen(true)
  }


  // =======================================================
  // OPEN EDIT
  // =======================================================

  function openEditDialog(price) {
    setEditingPrice(price)

    setForm({
      product_id:
        price.product_id || "",

      kin_mousse_price:
        String(
          price.kin_mousse_price ??
            ""
        ),

      maison_djo_margin_percent:
        String(
          price.maison_djo_margin_percent ??
            25
        ),

      depot_relais_margin_percent:
        String(
          price.depot_relais_margin_percent ??
            25
        ),

      terrain_margin_percent:
        String(
          price.terrain_margin_percent ??
            20
        ),

      is_active:
        Boolean(
          price.is_active
        ),

      notes:
        price.notes || "",
    })

    setDialogOpen(true)
  }


  // =======================================================
  // CLOSE DIALOG
  // =======================================================

  function closeDialog() {
    if (saving) {
      return
    }

    setDialogOpen(false)

    setEditingPrice(null)

    setForm(
      getDefaultForm()
    )
  }


  // =======================================================
  // UPDATE FORM
  // =======================================================

  function updateForm(
    field,
    value
  ) {
    setForm(
      (current) => ({
        ...current,
        [field]: value,
      })
    )
  }


  // =======================================================
  // PRODUCT CHANGE
  // =======================================================

  function handleProductChange(
    value
  ) {
    const product =
      formData.products.find(
        (item) =>
          String(item.id) ===
          String(value)
      )

    setForm(
      (current) => ({
        ...current,

        product_id:
          value,

        kin_mousse_price:
          product?.factory_price
            ? String(
                product.factory_price
              )
            : current.kin_mousse_price,
      })
    )
  }


  // =======================================================
  // BUILD PAYLOAD
  // =======================================================

  function buildPayload() {
    return {
      product_id:
        Number(
          form.product_id
        ),

      kin_mousse_price:
        Number(
          form.kin_mousse_price
        ),

      maison_djo_margin_percent:
        Number(
          form.maison_djo_margin_percent
        ),

      depot_relais_margin_percent:
        Number(
          form.depot_relais_margin_percent
        ),

      terrain_margin_percent:
        Number(
          form.terrain_margin_percent
        ),

      is_active:
        Boolean(
          form.is_active
        ),

      notes:
        form.notes?.trim() ||
        null,
    }
  }


  // =======================================================
  // VALIDATION
  // =======================================================

  function validateBeforeSave() {
    if (!form.product_id) {
      showError(
        "Sélectionnez un produit."
      )

      return false
    }

    if (
      toNumber(
        form.kin_mousse_price
      ) <= 0
    ) {
      showError(
        "Le prix Entreprise principale est obligatoire."
      )

      return false
    }

    if (
      toNumber(
        form.maison_djo_margin_percent
      ) < 0 ||
      toNumber(
        form.depot_relais_margin_percent
      ) < 0 ||
      toNumber(
        form.terrain_margin_percent
      ) < 0
    ) {
      showError(
        "Les marges ne peuvent pas être négatives."
      )

      return false
    }

    return true
  }


  // =======================================================
  // SAVE
  // =======================================================

  async function savePrice() {
    if (
      !validateBeforeSave()
    ) {
      return
    }

    try {
      setSaving(true)

      const payload =
        buildPayload()

      if (editingPrice) {
        await updateProductPrice(
          editingPrice.product_id,
          payload
        )

        showSuccess(
          "Prix produit modifiés avec succès."
        )
      } else {
        await createProductPrice(
          payload
        )

        showSuccess(
          "Prix produit configurés avec succès."
        )
      }

      setDialogOpen(false)
      setEditingPrice(null)

      setForm(
        getDefaultForm()
      )

      await loadPrices()
    } catch (error) {
      showError(
        getErrorMessage(
          error,
          "Impossible d'enregistrer les prix du produit."
        )
      )
    } finally {
      setSaving(false)
    }
  }


  // =======================================================
  // DEACTIVATE
  // =======================================================

  async function handleDeactivate(
    price
  ) {
    const ok =
      window.confirm(
        `Désactiver les prix du produit ${price.product_code} ?`
      )

    if (!ok) {
      return
    }

    try {
      await deactivateProductPrice(
        price.product_id
      )

      showSuccess(
        "Prix produit désactivés."
      )

      await loadPrices()
    } catch (error) {
      showError(
        getErrorMessage(
          error,
          "Impossible de désactiver les prix du produit."
        )
      )
    }
  }


  // =======================================================
  // RESET FILTERS
  // =======================================================

  async function resetFilters() {
    setFilters({
      search: "",
      configured: "",
      is_active: "",
    })

    setPage(0)
    setLoading(true)

    try {
      const list =
        await getProductPrices()

      setPrices(
        list.items || []
      )

      setSummary(
        list.summary || {}
      )
    } catch (error) {
      showError(
        getErrorMessage(
          error,
          "Impossible de réinitialiser la liste."
        )
      )
    } finally {
      setLoading(false)
    }
  }


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          minWidth: 0,
          width: "100%",

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root": {
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 2.5,
          },

          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5,
          },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              md:
                "minmax(0, 1fr) auto",
            },

            alignItems: "start",

            columnGap: 2,
            rowGap: 1.25,

            mb: 2.1,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Paramètres prix produits
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 820,
              }}
            >
              Définissez le prix de base Entreprise principale
              et les marges appliquées au Distributeur principal,
              à la Structure secondaire et au Terrain.
            </Typography>
          </Box>


          <Box
            sx={{
              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              gap: 1,

              width: {
                xs: "100%",
                md: "auto",
              },
            }}
          >
            <Button
              variant="outlined"
              startIcon={
                <RefreshIcon />
              }
              onClick={
                loadPage
              }
              disabled={
                loading
              }
              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  sm: 125,
                },
              }}
            >
              Actualiser
            </Button>

            <Button
              variant="contained"
              disableElevation
              startIcon={
                <AddIcon />
              }
              onClick={
                openCreateDialog
              }
              disabled={
                !canSave
              }
              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  sm: 190,
                },
              }}
            >
              Configurer un prix
            </Button>
          </Box>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mb: 1.6,

            borderRadius: 3,

            alignItems: "center",

            py: 0.35,

            bgcolor:
              "rgba(14, 165, 233, 0.025)",
          }}
        >
          Le prix Entreprise principale est la base.
          Les autres prix sont calculés automatiquement selon
          leurs marges en pourcentage.
        </Alert>


        {/* =================================================
            COMPACT KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              sm:
                "repeat(2, minmax(0, 1fr))",
              md:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 1.15,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Produits"
            value={
              summary.products_count ||
              0
            }
            caption="Produits actifs"
            accent="#2563eb"
            background="rgba(37, 99, 235, 0.09)"
            icon={
              <Inventory2RoundedIcon
                fontSize="small"
              />
            }
          />

          <MetricCard
            label="Configurés"
            value={
              summary.configured_products ||
              0
            }
            caption="Avec prix paramétrés"
            accent="#7c3aed"
            background="rgba(124, 58, 237, 0.09)"
            icon={
              <PriceChangeRoundedIcon
                fontSize="small"
              />
            }
          />

          <MetricCard
            label="Actifs"
            value={
              summary.active_products ||
              0
            }
            caption="Utilisables"
            accent="#16a34a"
            background="rgba(22, 163, 74, 0.09)"
            icon={
              <CheckCircleRoundedIcon
                fontSize="small"
              />
            }
          />

          <MetricCard
            label="Non configurés"
            value={
              summary.unconfigured_products ||
              0
            }
            caption="Prix à compléter"
            accent="#ea580c"
            background="rgba(234, 88, 12, 0.09)"
            icon={
              <PendingActionsRoundedIcon
                fontSize="small"
              />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 6px 18px rgba(15, 23, 42, 0.03)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(230px, 1.5fr) minmax(170px, 0.85fr) minmax(145px, 0.75fr) auto auto",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              label="Recherche produit"
              placeholder="Code ou nom..."
              value={
                filters.search
              }
              onChange={(
                event
              ) =>
                setFilters(
                  (current) => ({
                    ...current,

                    search:
                      event.target.value,
                  })
                )
              }
              fullWidth
            />


            <FormControl
              fullWidth
              size="small"
            >
              <InputLabel>
                Configuration
              </InputLabel>

              <Select
                label="Configuration"
                value={
                  filters.configured
                }
                onChange={(
                  event
                ) =>
                  setFilters(
                    (current) => ({
                      ...current,

                      configured:
                        event.target.value,
                    })
                  )
                }
              >
                <MenuItem value="">
                  Tous
                </MenuItem>

                <MenuItem value="configured">
                  Configurés
                </MenuItem>

                <MenuItem value="unconfigured">
                  Non configurés
                </MenuItem>
              </Select>
            </FormControl>


            <FormControl
              fullWidth
              size="small"
            >
              <InputLabel>
                Statut
              </InputLabel>

              <Select
                label="Statut"
                value={
                  filters.is_active
                }
                onChange={(
                  event
                ) =>
                  setFilters(
                    (current) => ({
                      ...current,

                      is_active:
                        event.target.value,
                    })
                  )
                }
              >
                <MenuItem value="">
                  Tous
                </MenuItem>

                <MenuItem value="true">
                  Actif
                </MenuItem>

                <MenuItem value="false">
                  Inactif
                </MenuItem>
              </Select>
            </FormControl>


            <Button
              variant="contained"
              disableElevation
              onClick={
                loadPrices
              }
              disabled={
                loading
              }
              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  lg: 96,
                },
              }}
            >
              Appliquer
            </Button>


            <Button
              variant="outlined"
              onClick={
                resetFilters
              }
              disabled={
                loading
              }
              sx={{
                minHeight: 40,

                minWidth: {
                  xs: "100%",
                  lg: 110,
                },
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            PRICE REGISTER
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            minWidth: 0,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >

          {/* TABLE TITLE */}

          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Grille tarifaire produits
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Prix et marges par canal de distribution
              </Typography>
            </Box>


            <Chip
              size="small"
              variant="outlined"
              label={`${prices.length} produit${
                prices.length > 1
                  ? "s"
                  : ""
              }`}
              sx={{
                fontWeight: 800,
              }}
            />
          </Box>


          {/* =================================================
              DESKTOP TABLE
          ================================================= */}

          {!isMdDown ? (
            <>
              <TableContainer
                sx={{
                  width: "100%",

                  overflowX: "auto",
                }}
              >
                <Table
                  size="small"
                  sx={{
                    width: "100%",

                    tableLayout: "fixed",

                    "& .MuiTableCell-head":
                      {
                        bgcolor: "#f8fafc",

                        color: "#334155",

                        fontWeight: 900,

                        fontSize:
                          "0.77rem",

                        lineHeight: 1.2,

                        py: 1.05,

                        borderColor:
                          "rgba(148, 163, 184, 0.18)",
                      },

                    "& .MuiTableCell-body":
                      {
                        borderColor:
                          "rgba(148, 163, 184, 0.14)",

                        py: 1.05,
                      },

                    "& .MuiTableRow-root:hover":
                      {
                        bgcolor:
                          "#f8fbff",
                      },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: "15%",
                        }}
                      >
                        Produit
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          width: "12%",
                        }}
                      >
                        Prix Entreprise
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          width: "14%",
                        }}
                      >
                        Distributeur
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          width: "14%",
                        }}
                      >
                        Structure secondaire
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          width: "11%",
                        }}
                      >
                        Terrain
                      </TableCell>

                      <TableCell
                        sx={{
                          width: "10%",
                        }}
                      >
                        Statut
                      </TableCell>

                      <TableCell
                        sx={{
                          width: "10%",

                          display: {
                            md: "none",
                            lg: "table-cell",
                          },
                        }}
                      >
                        Notes
                      </TableCell>

                      <TableCell
                        align="right"
                        sx={{
                          width: "14%",
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>


                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="center"
                          sx={{
                            py: 5,
                          }}
                        >
                          <Typography
                            color="text.secondary"
                          >
                            Chargement des prix...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : paginatedPrices.length ===
                      0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="center"
                          sx={{
                            py: 5,
                          }}
                        >
                          <Typography
                            color="text.secondary"
                          >
                            Aucun produit trouvé.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedPrices.map(
                        (price) => {
                          const status =
                            getPriceStatus(
                              price
                            )

                          return (
                            <TableRow
                              key={
                                price.product_id
                              }
                              hover
                            >

                              {/* PRODUCT */}

                              <TableCell>
                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                  color="#0f172a"
                                  noWrap
                                  title={
                                    price.product_code ||
                                    ""
                                  }
                                >
                                  {price.product_code ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{
                                    display:
                                      "block",
                                  }}
                                  title={
                                    price.product_name ||
                                    ""
                                  }
                                >
                                  {price.product_name ||
                                    "-"}
                                </Typography>
                              </TableCell>


                              {/* BASE */}

                              <TableCell
                                align="right"
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatMoney(
                                    price.kin_mousse_price
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Base
                                </Typography>
                              </TableCell>


                              {/* DISTRIBUTOR */}

                              <TableCell
                                align="right"
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatMoney(
                                    price.maison_djo_price
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{
                                    display:
                                      "block",
                                  }}
                                >
                                  Marge{" "}
                                  {formatPercent(
                                    price.maison_djo_margin_percent
                                  )}
                                  %
                                </Typography>
                              </TableCell>


                              {/* RELAY */}

                              <TableCell
                                align="right"
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatMoney(
                                    price.depot_relais_price
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{
                                    display:
                                      "block",
                                  }}
                                >
                                  Marge{" "}
                                  {formatPercent(
                                    price.depot_relais_margin_percent
                                  )}
                                  %
                                </Typography>
                              </TableCell>


                              {/* TERRAIN */}

                              <TableCell
                                align="right"
                              >
                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatMoney(
                                    price.terrain_price
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{
                                    display:
                                      "block",
                                  }}
                                >
                                  Marge{" "}
                                  {formatPercent(
                                    price.terrain_margin_percent
                                  )}
                                  %
                                </Typography>
                              </TableCell>


                              {/* STATUS */}

                              <TableCell>
                                <Chip
                                  size="small"

                                  label={
                                    status.label
                                  }

                                  color={
                                    status.color
                                  }

                                  variant={
                                    status.variant
                                  }

                                  sx={{
                                    maxWidth:
                                      "100%",

                                    fontWeight:
                                      800,
                                  }}
                                />
                              </TableCell>


                              {/* NOTES */}

                              <TableCell
                                sx={{
                                  display: {
                                    md: "none",
                                    lg: "table-cell",
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                  title={
                                    price.notes ||
                                    ""
                                  }
                                >
                                  {price.notes ||
                                    "—"}
                                </Typography>
                              </TableCell>


                              {/* ACTIONS */}

                              <TableCell
                                align="right"
                              >
                                <Stack
                                  direction="column"
                                  spacing={0.55}
                                  alignItems="stretch"
                                  sx={{
                                    maxWidth: 108,

                                    ml: "auto",
                                  }}
                                >
                                  <Button
                                    size="small"

                                    variant="contained"

                                    disableElevation

                                    startIcon={
                                      <EditIcon />
                                    }

                                    onClick={() =>
                                      openEditDialog(
                                        price
                                      )
                                    }

                                    disabled={
                                      !canSave
                                    }

                                    sx={{
                                      minHeight: 30,

                                      fontSize:
                                        "0.75rem",
                                    }}
                                  >
                                    Modifier
                                  </Button>

                                  <Button
                                    size="small"

                                    color="error"

                                    variant="outlined"

                                    startIcon={
                                      <BlockIcon />
                                    }

                                    onClick={() =>
                                      handleDeactivate(
                                        price
                                      )
                                    }

                                    disabled={
                                      !permissions.can_delete ||
                                      !price.configured
                                    }

                                    sx={{
                                      minHeight: 30,

                                      fontSize:
                                        "0.75rem",
                                    }}
                                  >
                                    Désactiver
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          )
                        }
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>


              {/* STRICT DESKTOP PAGINATION */}

              <TablePagination
                component="div"

                count={
                  prices.length
                }

                page={page}

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPage(
                    newPage
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  borderTop:
                    "1px solid",

                  borderColor:
                    "divider",

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,

                      px: 2,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-displayedRows":
                    {
                      fontSize:
                        "0.82rem",

                      fontWeight: 700,

                      color:
                        "#475569",
                    },
                }}
              />
            </>
          ) : (

            /* =================================================
               TABLET / MOBILE
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.15,
                  sm: 1.35,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des prix...
                  </Typography>
                </Box>
              ) : paginatedPrices.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,

                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun produit trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack
                  spacing={1.1}
                >
                  {paginatedPrices.map(
                    (price) => {
                      const status =
                        getPriceStatus(
                          price
                        )

                      return (
                        <Paper
                          key={
                            price.product_id
                          }

                          variant="outlined"

                          sx={{
                            p: {
                              xs: 1.4,
                              sm: 1.6,
                            },

                            borderRadius: 3,

                            bgcolor: "#ffffff",

                            borderColor:
                              "rgba(148, 163, 184, 0.22)",
                          }}
                        >
                          <Stack
                            spacing={1.35}
                          >

                            {/* TOP */}

                            <Box
                              sx={{
                                display: "flex",

                                justifyContent:
                                  "space-between",

                                alignItems:
                                  "flex-start",

                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  minWidth: 0,
                                }}
                              >
                                <Typography
                                  fontWeight={900}
                                  color="#0f172a"
                                >
                                  {price.product_name ||
                                    "-"}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {price.product_code ||
                                    "-"}
                                </Typography>
                              </Box>

                              <Chip
                                size="small"

                                label={
                                  status.label
                                }

                                color={
                                  status.color
                                }

                                variant={
                                  status.variant
                                }

                                sx={{
                                  fontWeight: 800,
                                }}
                              />
                            </Box>


                            {/* BASE */}

                            <Paper
                              variant="outlined"
                              sx={{
                                px: 1.25,
                                py: 1,

                                borderRadius: 2.5,

                                bgcolor: "#f8fafc",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                              >
                                Prix Entreprise principale
                              </Typography>

                              <Typography
                                fontWeight={900}
                                color="#0f172a"
                              >
                                {formatMoney(
                                  price.kin_mousse_price
                                )}
                              </Typography>
                            </Paper>


                            {/* CHANNELS */}

                            <Box
                              sx={{
                                display: "grid",

                                gridTemplateColumns:
                                  {
                                    xs:
                                      "1fr",

                                    sm:
                                      "repeat(3, minmax(0, 1fr))",
                                  },

                                gap: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                >
                                  Distributeur
                                </Typography>

                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatMoney(
                                    price.maison_djo_price
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Marge{" "}
                                  {formatPercent(
                                    price.maison_djo_margin_percent
                                  )}
                                  %
                                </Typography>
                              </Box>


                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                >
                                  Structure secondaire
                                </Typography>

                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatMoney(
                                    price.depot_relais_price
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Marge{" "}
                                  {formatPercent(
                                    price.depot_relais_margin_percent
                                  )}
                                  %
                                </Typography>
                              </Box>


                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                >
                                  Terrain
                                </Typography>

                                <Typography
                                  variant="body2"
                                  fontWeight={900}
                                >
                                  {formatMoney(
                                    price.terrain_price
                                  )}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Marge{" "}
                                  {formatPercent(
                                    price.terrain_margin_percent
                                  )}
                                  %
                                </Typography>
                              </Box>
                            </Box>


                            {/* NOTES */}

                            {price.notes ? (
                              <Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={700}
                                >
                                  Notes
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    mt: 0.2,

                                    overflowWrap:
                                      "anywhere",
                                  }}
                                >
                                  {price.notes}
                                </Typography>
                              </Box>
                            ) : null}


                            {/* ACTIONS */}

                            <Box
                              sx={{
                                display: "grid",

                                gridTemplateColumns:
                                  {
                                    xs: "1fr",

                                    sm:
                                      "repeat(2, minmax(0, 1fr))",
                                  },

                                gap: 0.8,
                              }}
                            >
                              <Button
                                size="small"

                                variant="contained"

                                disableElevation

                                startIcon={
                                  <EditIcon />
                                }

                                onClick={() =>
                                  openEditDialog(
                                    price
                                  )
                                }

                                disabled={
                                  !canSave
                                }
                              >
                                Modifier
                              </Button>

                              <Button
                                size="small"

                                color="error"

                                variant="outlined"

                                startIcon={
                                  <BlockIcon />
                                }

                                onClick={() =>
                                  handleDeactivate(
                                    price
                                  )
                                }

                                disabled={
                                  !permissions.can_delete ||
                                  !price.configured
                                }
                              >
                                Désactiver
                              </Button>
                            </Box>
                          </Stack>
                        </Paper>
                      )
                    }
                  )}
                </Stack>
              )}


              {/* STRICT MOBILE PAGINATION */}

              <TablePagination
                component="div"

                count={
                  prices.length
                }

                page={page}

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPage(
                    newPage
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor: "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,

                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          CONFIGURATION DIALOG
      ================================================= */}

      <Dialog
        open={
          dialogOpen
        }

        onClose={
          closeDialog
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "90dvh",
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {editingPrice
              ? "Modifier les prix du produit"
              : "Configurer les prix du produit"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.35,
            }}
          >
            Définissez le prix de base et les marges appliquées
            automatiquement aux différents canaux de distribution.
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          <Stack
            spacing={2}
            sx={{
              pt: 0.5,
            }}
          >

            {/* PRODUCT + BASE */}

            <Box
              sx={{
                display: "grid",

                gridTemplateColumns: {
                  xs: "1fr",

                  md:
                    "minmax(0, 1.2fr) minmax(0, 0.8fr)",
                },

                gap: 2,
              }}
            >
              <FormControl
                fullWidth
                size="small"
              >
                <InputLabel>
                  Produit *
                </InputLabel>

                <Select
                  label="Produit *"

                  value={
                    form.product_id
                  }

                  onChange={(
                    event
                  ) =>
                    handleProductChange(
                      event.target.value
                    )
                  }

                  disabled={
                    Boolean(
                      editingPrice
                    ) ||
                    saving
                  }
                >
                  {(formData.products || []).map(
                    (product) => (
                      <MenuItem
                        key={
                          product.id
                        }

                        value={
                          product.id
                        }
                      >
                        {product.code} -{" "}
                        {product.name}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>


              <TextField
                size="small"

                label="Prix Entreprise principale *"

                type="number"

                value={
                  form.kin_mousse_price
                }

                onChange={(
                  event
                ) =>
                  updateForm(
                    "kin_mousse_price",
                    event.target.value
                  )
                }

                disabled={
                  !canManageFactory ||
                  saving
                }

                fullWidth

                slotProps={{
                  htmlInput: {
                    min: 0,
                    step: 0.01,
                  },
                }}

                helperText={
                  selectedProduct
                    ? `Prix produit actuel : ${formatMoney(
                        selectedProduct.factory_price
                      )}`
                    : "Prix de base."
                }
              />
            </Box>


            {/* DISTRIBUTOR */}

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Distributeur principal
              </Typography>

              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.4,
                }}
              >
                <TextField
                  size="small"

                  label="Marge (%)"

                  type="number"

                  value={
                    form.maison_djo_margin_percent
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "maison_djo_margin_percent",
                      event.target.value
                    )
                  }

                  disabled={
                    !canManageMaisonDjo ||
                    saving
                  }

                  fullWidth

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}
                />

                <TextField
                  size="small"

                  label="Prix calculé"

                  value={
                    formatMoney(
                      computedPrices.maisonDjoPrice
                    )
                  }

                  fullWidth

                  disabled
                />
              </Box>
            </Paper>


            {/* RELAY */}

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Structure secondaire
              </Typography>

              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.4,
                }}
              >
                <TextField
                  size="small"

                  label="Marge (%)"

                  type="number"

                  value={
                    form.depot_relais_margin_percent
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "depot_relais_margin_percent",
                      event.target.value
                    )
                  }

                  disabled={
                    !canManageDepotRelais ||
                    saving
                  }

                  fullWidth

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}
                />

                <TextField
                  size="small"

                  label="Prix calculé"

                  value={
                    formatMoney(
                      computedPrices.depotRelaisPrice
                    )
                  }

                  fullWidth

                  disabled
                />
              </Box>
            </Paper>


            {/* TERRAIN */}

            <Paper
              variant="outlined"
              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Terrain / Agent commercial
              </Typography>

              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.4,
                }}
              >
                <TextField
                  size="small"

                  label="Marge (%)"

                  type="number"

                  value={
                    form.terrain_margin_percent
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "terrain_margin_percent",
                      event.target.value
                    )
                  }

                  disabled={
                    !canManageTerrain ||
                    saving
                  }

                  fullWidth

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}
                />

                <TextField
                  size="small"

                  label="Prix calculé"

                  value={
                    formatMoney(
                      computedPrices.terrainPrice
                    )
                  }

                  fullWidth

                  disabled
                />
              </Box>
            </Paper>


            {/* NOTES */}

            <TextField
              label="Notes"

              value={
                form.notes
              }

              onChange={(
                event
              ) =>
                updateForm(
                  "notes",
                  event.target.value
                )
              }

              fullWidth

              multiline

              minRows={3}

              disabled={
                saving
              }

              placeholder="Informations complémentaires sur cette configuration tarifaire..."
            />


            {/* ACTIVE */}

            <Paper
              variant="outlined"
              sx={{
                px: 1.5,

                py: 0.7,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Box
                sx={{
                  display: "flex",

                  alignItems: "center",

                  gap: 1,
                }}
              >
                <Switch
                  checked={
                    Boolean(
                      form.is_active
                    )
                  }

                  onChange={(
                    event
                  ) =>
                    updateForm(
                      "is_active",
                      event.target.checked
                    )
                  }

                  disabled={
                    saving
                  }
                />

                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={800}
                  >
                    Prix actif
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Configuration utilisable dans les ventes et
                    les opérations tarifaires.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              closeDialog
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>

          <Button
            variant="contained"

            disableElevation

            onClick={
              savePrice
            }

            disabled={
              loading ||
              saving ||
              !canSave
            }
          >
            {saving
              ? "Enregistrement..."
              : editingPrice
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          closeSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }

          onClose={
            closeSnackbar
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default ProductPricesPage